import type { FileUploadAndParseProps } from "@fronterahealth/frontera-ui-components";

import { useCreateTemplateExportFile, useUpdateTemplateExportFile } from "@api/rest/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";

export const useGetS3BucketTemplateExportFileUrlRetriever = ({
  templateId,
}: {
  templateId: number;
}): FileUploadAndParseProps["S3BucketUrlRetriever"] => {
  const createFileMutation = useCreateTemplateExportFile();

  return async ({ fileName, mimeType, fileSize }) => {
    let bucket = "<missing-bucket-url>";
    let fileId = "<missing-file-id>";

    try {
      const res = await createFileMutation.mutateAsync({
        data: {
          template_id: templateId,
          file_size: fileSize,
          mime_type: mimeType,
          original_file_name: fileName,
        },
      });

      if (res.status !== 200) {
        notifyError("Error creating export file upload URL");
        throw new Error("Error creating export file upload URL");
      }

      bucket = res.data.file_url;
      fileId = String(templateId);
      return { bucket, fileId };
    } catch (err) {
      throw new Error(String(err));
    }
  };
};

export const useGetS3BucketUploadTemplateExportFileCallback = ({
  templateId,
  refetch,
}: {
  templateId: number;
  refetch: () => Promise<void> | unknown;
}): FileUploadAndParseProps["S3BucketUploadCallback"] => {
  const updateFileUrlMutation = useUpdateTemplateExportFile();

  const uploadWithRetry = async ({
    file,
    mimeType,
    url,
    retries = 5,
    attempt = 1,
  }: {
    file: File;
    mimeType: string;
    url: string;
    retries?: number;
    attempt?: number;
  }): Promise<Response> => {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": mimeType },
        body: file,
      });

      if (response.status === 200) return response;
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    } catch (err) {
      if (attempt < retries) {
        const backoff = 100 * 2 ** (attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return uploadWithRetry({ file, mimeType, url, retries, attempt: attempt + 1 });
      }
      throw err;
    }
  };

  return async ({ file, mimeType, url }) => {
    try {
      await uploadWithRetry({ file, mimeType, url });

      const res = await updateFileUrlMutation.mutateAsync({
        data: { template_id: templateId, original_file_name: file.name },
      });

      if (res.status !== 200) {
        notifyError("Could not confirm export file upload.");
        return;
      }

      await refetch();
    } catch (err) {
      throw new Error(String(err));
    }
  };
};
